/**
 * StateDeltaDecoder - Client-Side Delta State Reconstruction
 *
 * F8: Delta Encoding
 *
 * Receives delta or full state broadcasts from the server and reconstructs
 * complete CombatState objects for the rendering pipeline.
 *
 * The decoder maintains the last known full state and applies deltas
 * (changed/removed units, updated projectiles/events) to produce a
 * complete state that existing consumers (StateDiffDetector, StateUpdateBuffer,
 * renderers) can use unchanged.
 *
 * Protocol:
 * - 'full' broadcasts replace the entire state
 * - 'delta' broadcasts patch the last known state
 * - If a delta arrives before any full state, it's buffered until the first
 *   full state arrives
 */

import type {
  CombatState,
  CombatCreature
} from '@drawn-of-war/shared/src/types/combat';

/**
 * Delta update received from the server.
 * Mirrors the backend StateDeltaEncoder's CombatStateDelta type.
 */
export interface CombatStateDelta {
  type: 'delta';
  tick: number;
  matchId: string;
  status?: CombatState['status'];
  changedUnits?: CombatCreature[];
  removedUnitIds?: string[];
  projectiles: CombatState['projectiles'];
  events: CombatState['events'];
  statistics: CombatState['statistics'];
  endTime?: number;
}

/** Full state broadcast tagged with type discriminator */
export interface CombatStateFullBroadcast extends CombatState {
  type: 'full';
}

/** Union type for incoming broadcasts */
export type CombatBroadcast = CombatStateDelta | CombatStateFullBroadcast;

export class StateDeltaDecoder {
  private currentState: CombatState | null = null;
  private unitIndex: Map<string, CombatCreature> = new Map();

  // Stats
  private deltasApplied: number = 0;
  private fullStatesReceived: number = 0;

  /**
   * Apply a broadcast (full or delta) and return the reconstructed full state.
   * Returns null if a delta arrives before any full state.
   */
  apply(broadcast: CombatBroadcast): CombatState | null {
    if (broadcast.type === 'full') {
      return this.applyFullState(broadcast);
    }
    return this.applyDelta(broadcast);
  }

  /**
   * Get the current reconstructed state (or null if not yet initialized).
   */
  getCurrentState(): CombatState | null {
    return this.currentState;
  }

  /**
   * Reset decoder state.
   */
  reset(): void {
    this.currentState = null;
    this.unitIndex.clear();
    this.deltasApplied = 0;
    this.fullStatesReceived = 0;
  }

  /**
   * Get decoder statistics.
   */
  getStats(): {
    deltasApplied: number;
    fullStatesReceived: number;
    unitCount: number;
  } {
    return {
      deltasApplied: this.deltasApplied,
      fullStatesReceived: this.fullStatesReceived,
      unitCount: this.unitIndex.size
    };
  }

  // === Private Methods ===

  private applyFullState(broadcast: CombatStateFullBroadcast): CombatState {
    // Strip the 'type' discriminator to get a clean CombatState
    const { type, ...state } = broadcast;
    this.currentState = state as CombatState;

    // Rebuild unit index
    this.unitIndex.clear();
    for (const unit of this.currentState.units) {
      this.unitIndex.set(unit.unitId, unit);
    }

    this.fullStatesReceived++;
    return this.currentState;
  }

  private applyDelta(delta: CombatStateDelta): CombatState | null {
    if (!this.currentState) {
      // Can't apply delta without a base state
      return null;
    }

    // Update tick
    this.currentState.tick = delta.tick;
    this.currentState.matchId = delta.matchId;

    // Status change
    if (delta.status !== undefined) {
      this.currentState.status = delta.status;
    }

    // End time
    if (delta.endTime !== undefined) {
      this.currentState.endTime = delta.endTime;
    }

    // Apply unit changes
    if (delta.changedUnits) {
      for (const unit of delta.changedUnits) {
        this.unitIndex.set(unit.unitId, unit);
      }
    }

    // Remove units
    if (delta.removedUnitIds) {
      for (const unitId of delta.removedUnitIds) {
        this.unitIndex.delete(unitId);
      }
    }

    // Rebuild units array from index (only if changes occurred)
    if (delta.changedUnits || delta.removedUnitIds) {
      this.currentState.units = Array.from(this.unitIndex.values());
    }

    // Replace volatile arrays
    this.currentState.projectiles = delta.projectiles;
    this.currentState.events = delta.events;
    this.currentState.statistics = delta.statistics;

    this.deltasApplied++;
    return this.currentState;
  }
}
